// ─────────────────────────────────────────────────────────────────
// Glass-i — Stock Ledger LIST page (Stage 6P.5).
//
// Server-paginated, read-only ledger at /erp/inventory/stock-ledger.
//
// The backend is the authority for every number on this page — qty,
// sqmt, sqft, total_length, posted_at, movement_no allocation, etc.
// This page only reads (GET /api/erp/inventory/stock-ledger) and
// shapes the response for the AntD grid. There are no row actions,
// no mutations and no inline edits.
//
// Columns (brief):
//   Movement No · Movement Date · Movement Type · Source Type
//   Source No · Item Type · Stock Name · Qty · SQMT · SQFT
//   Total Length · UOM · Warehouse · Posted By · Posted At · Remarks
//
// Toolbar:
//   Search (item / stock name)
//   Movement type · Source type · Warehouse · Date range
//   Clear · Refresh.
//
// Pagination: 25 / 50 / 100.
//
// ADDITIVE-ONLY: reuses the existing `.grn-*` CSS shell (slate header
// strip, sticky toolbar, 22 px row density) for visual symmetry with
// the rest of the ERP grids. The wrapper class is still `.grn-shell`
// so the shared CSS wins without us having to duplicate styles.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Input, Select, Space,
  Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, DatabaseOutlined
} from '@ant-design/icons';

import { inventoryApi } from './inventoryApi';
import {
  MOVEMENT_TYPES,
  SOURCE_TYPES,
  normalizeStockLedgerRow,
  movementTypeLabel,
  itemTypeLabel,
  itemTypeColor
} from './inventoryHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const MOVEMENT_TYPE_OPTIONS = MOVEMENT_TYPES.map((v) => ({
  value: v,
  label: movementTypeLabel(v)
}));

const SOURCE_TYPE_LABELS = {
  GRN:           'GRN',
  PURCHASE_BILL: 'Purchase Bill'
};

const SOURCE_TYPE_OPTIONS = SOURCE_TYPES.map((v) => ({
  value: v,
  label: SOURCE_TYPE_LABELS[v] || v
}));

function sourceTypeLabel(value) {
  if (!value) return '';
  const key = String(value).trim().toUpperCase();
  return SOURCE_TYPE_LABELS[key] || key;
}

const MOVEMENT_TYPE_COLORS = {
  PURCHASE_INWARD:          'green',
  PURCHASE_INWARD_REVERSAL: 'orange',
  ADJUSTMENT:               'geekblue'
};

function movementTypeColor(value) {
  if (!value) return 'default';
  const key = String(value).trim().toUpperCase();
  return MOVEMENT_TYPE_COLORS[key] || 'default';
}

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day    = String(d.getDate()).padStart(2, '0');
  const month  = d.toLocaleString('en-US', { month: 'short' });
  const year   = d.getFullYear();
  const hh     = String(d.getHours()).padStart(2, '0');
  const mm     = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hh}:${mm}`;
}

// Locale-aware numeric — en-IN grouping, 4 fraction digits for the
// dimensional columns (sqmt / sqft / total length). Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "0.0000".
function fmtNumber(value, decimals) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  } catch {
    return n.toFixed(decimals);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Resolve the user-facing source document number from whichever
// snapshot key the backend chose to put on the ledger row.
function resolveSourceNo(row) {
  return (
    pickField(row, 'source_no',            'sourceNo')          ||
    pickField(row, 'grn_no_snapshot',      'grnNoSnapshot')     ||
    pickField(row, 'goods_receipt_number', 'goodsReceiptNumber')||
    pickField(row, 'grn_no',               'grnNo')             ||
    pickField(row, 'bill_no_snapshot',     'billNoSnapshot')    ||
    pickField(row, 'purchase_bill_no',     'purchaseBillNo')    ||
    pickField(row, 'bill_no',              'billNo')            ||
    null
  );
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Prefer a human name; never fall through to the raw posted_by UUID.
// (If the poster's user record was later removed, the name join is null —
// in that case show a dash rather than an unreadable id.)
function resolvePostedBy(row) {
  const v =
    pickField(row, 'posted_by_name',     'postedByName')     ||
    pickField(row, 'posted_by_username', 'postedByUsername') ||
    pickField(row, 'posted_by_display',  'postedByDisplay')  ||
    pickField(row, 'posted_by',          'postedBy')         ||
    null;
  if (v && UUID_RE.test(String(v).trim())) return null;
  return v;
}

function resolveWarehouseName(row) {
  return (
    pickField(row, 'warehouse_name', 'warehouseName') ||
    pickField(row, 'warehouse_code', 'warehouseCode') ||
    null
  );
}

function resolveStockName(row) {
  return (
    pickField(row, 'stock_name',       'stockName')       ||
    pickField(row, 'item_description', 'itemDescription') ||
    pickField(row, 'item_desc',        'itemDesc')        ||
    pickField(row, 'item_name',        'itemName')        ||
    null
  );
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function StockLedgerPage() {
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.inventory.stock_ledger.view` covers admin/manager/staff/viewer
  // per the Stage 6P.5 permissions registry.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView = hasPerm('erp.inventory.stock_ledger.view');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [movementType, setMovementType] = useState(null);
  const [sourceType,   setSourceType]   = useState(null);
  const [warehouseId,  setWarehouseId]  = useState(null);
  const [dateRange,    setDateRange]    = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // Derive the warehouse filter dropdown options from the rows
  // returned so far. The Stage 6P.5 backend doesn't ship a warehouse
  // master list endpoint, so we keep this client-side and tolerant —
  // when the page loads against a tenant that hasn't posted anything
  // yet the dropdown will be empty, which is fine.
  const warehouseOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      const id   = pickField(r, 'warehouse_id', 'warehouseId');
      const name = resolveWarehouseName(r);
      if (!id) continue;
      const key = String(id);
      if (!seen.has(key)) {
        seen.set(key, { value: key, label: name || key });
      }
    }
    return Array.from(seen.values());
  }, [rows]);

  // ── Debounced search wiring ──────────────────────────────────
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      // Send under BOTH `q` (brief language) and `search` so a
      // permissive backend Joi schema can pick whichever it knows.
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      if (movementType) params.movementType = movementType;
      if (sourceType)   params.sourceType   = sourceType;
      if (warehouseId)  params.warehouseId  = warehouseId;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await inventoryApi.getStockLedger(params);
      const rawList = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : [];
      const count = Array.isArray(out) ? rawList.length
        : Number(out?.total ?? out?.count ?? rawList.length) || 0;

      // Normalize every row up front so the column renderers can
      // read a single canonical camelCase shape.
      const normalized = rawList
        .map((r) => normalizeStockLedgerRow(r))
        .filter(Boolean);

      setRows(normalized);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load stock ledger.'),
        key: 'inv-ledger'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, movementType, sourceType, warehouseId, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onMovementType = (v) => { setMovementType(v || null); setPage(1); };
  const onSourceType   = (v) => { setSourceType(v || null);   setPage(1); };
  const onWarehouse    = (v) => { setWarehouseId(v || null);  setPage(1); };
  const onDates        = (v) => { setDateRange(v || null);    setPage(1); };
  const onReset        = () => {
    setSearchInput('');
    setSearch('');
    setMovementType(null);
    setSourceType(null);
    setWarehouseId(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Movement No',
      dataIndex: 'movementNo',
      width:     180,
      fixed:     'left',
      render: (_v, row) => {
        const no = pickField(row, 'movement_no', 'movementNo') || '—';
        return <span className="grn-mono">{no}</span>;
      }
    },
    {
      title:     'Movement Date',
      dataIndex: 'movementDate',
      width:     130,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'movement_date', 'movementDate'))}
        </span>
      )
    },
    {
      title:     'Movement Type',
      dataIndex: 'movementType',
      width:     160,
      render: (_v, row) => {
        const v = pickField(row, 'movement_type', 'movementType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={movementTypeColor(v)} className="grn-status-tag">
            {movementTypeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Source Type',
      dataIndex: 'sourceType',
      width:     130,
      render: (_v, row) => {
        const v = pickField(row, 'source_type', 'sourceType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color="geekblue" className="grn-status-tag">
            {sourceTypeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Source No',
      dataIndex: 'sourceNo',
      width:     170,
      render: (_v, row) => {
        const no = resolveSourceNo(row);
        if (!no) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{no}</span>;
      }
    },
    {
      title:     'Item Type',
      dataIndex: 'itemType',
      width:     120,
      render: (_v, row) => {
        const v = pickField(row, 'item_type', 'itemType');
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <Tag color={itemTypeColor(v)} className="grn-status-tag">
            {itemTypeLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Stock Name',
      dataIndex: 'stockName',
      render: (_v, row) => {
        const name = resolveStockName(row) || '—';
        return (
          <Tooltip title={name}>
            <span className="grn-supplier__name">{name}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'Qty',
      dataIndex: 'qty',
      width:     100,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtNumber(pickField(row, 'qty', 'qty'), 4)}
        </span>
      )
    },
    {
      title:     'SQMT',
      dataIndex: 'sqmt',
      width:     110,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtNumber(pickField(row, 'sqmt', 'sqmt'), 3)}
        </span>
      )
    },
    {
      title:     'SQFT',
      dataIndex: 'sqft',
      width:     110,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtNumber(pickField(row, 'sqft', 'sqft'), 3)}
        </span>
      )
    },
    {
      title:     'Total Length',
      dataIndex: 'totalLength',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtNumber(pickField(row, 'total_length', 'totalLength'), 4)}
        </span>
      )
    },
    {
      title:     'UOM',
      dataIndex: 'uom',
      width:     90,
      render: (_v, row) => {
        const v = pickField(row, 'uom', 'uom');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Warehouse',
      dataIndex: 'warehouseName',
      width:     180,
      render: (_v, row) => {
        const name = resolveWarehouseName(row);
        if (!name) return <span className="grn-muted">—</span>;
        return (
          <Tooltip title={name}>
            <span>{name}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'Posted By',
      dataIndex: 'postedBy',
      width:     150,
      render: (_v, row) => {
        const by = resolvePostedBy(row);
        if (!by) return <span className="grn-muted">—</span>;
        return (
          <Tooltip title={by}>
            <span>{by}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     'Posted At',
      dataIndex: 'postedAt',
      width:     160,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDateTime(pickField(row, 'posted_at', 'postedAt'))}
        </span>
      )
    },
    {
      title:     'Remarks',
      dataIndex: 'remarks',
      width:     240,
      render: (_v, row) => {
        const remarks = pickField(row, 'remarks', 'remarks');
        if (!remarks) return <span className="grn-muted">—</span>;
        return (
          <Tooltip title={remarks}>
            <span className="grn-supplier__name">{remarks}</span>
          </Tooltip>
        );
      }
    }
  ]), []);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view the stock ledger."
          description="Ask your master account to grant erp.inventory.stock_ledger.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><DatabaseOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Stock Ledger</span>
            <span className="grn-headstrip__subtitle">
              Append-only ledger of every inventory movement — purchase
              inwards, reversals and adjustments. Backend remains the
              authority for posted qty, area conversions and movement-no
              allocation; this page is read-only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search item, stock name, movement no…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All movement types"
          value={movementType || undefined}
          onChange={onMovementType}
          options={MOVEMENT_TYPE_OPTIONS}
          style={{ width: 180 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All source types"
          value={sourceType || undefined}
          onChange={onSourceType}
          options={SOURCE_TYPE_OPTIONS}
          style={{ width: 160 }}
        />
        <Select
          allowClear
          showSearch
          size="small"
          placeholder="All warehouses"
          value={warehouseId || undefined}
          onChange={onWarehouse}
          options={warehouseOptions}
          optionFilterProp="label"
          style={{ width: 180 }}
          notFoundContent="No warehouses on this page"
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id') ||
              pickField(r, 'movement_no', 'movementNo') ||
              `${pickField(r, 'source_id', 'sourceId') || ''}:${pickField(r, 'source_line_id', 'sourceLineId') || ''}`
            }
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No stock movements yet — post an inward from a GRN or Purchase Bill to begin.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
